'use client';

import { Box, Button, Typography } from '@mui/material';
import {
  Container,
  DesktopNav,
  LinkStyled,
  Logo,
  MenuBlockWrapper,
  MenuWrapper,
  MobileControls,
} from './styles';
import { MENU_ITEMS } from '@/constants';
import { FC, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { LogoHeader } from '@/components';
import { useIsProd, useSearchParamsCompat } from '@/hooks';
import { BurgerMenu } from './BurgerMenu';
import { useTranslations } from 'next-intl';
import { AuthButton } from './AuthButton';
import { LanguageSwitcher } from './LanguageSwitcher';
import { normalizePathnameWithoutLocale } from '@/utils/normalizePathnameWithoutLocale';

export const Header: FC = () => {
  const t = useTranslations();
  const isProd = useIsProd();

  const [isBurgerOpen, setIsBurgerOpen] = useState(false);
  const pathname = usePathname();
  const normalizedPathname = useMemo(() => normalizePathnameWithoutLocale(pathname), [pathname]);

  const searchParams = useSearchParamsCompat();
  const offerType = searchParams.get('offerType');
  const fullPath = `${normalizedPathname}${offerType ? `?offerType=${offerType}` : ''}`;

  const visibleMenuItems = useMemo(
    () => MENU_ITEMS.filter((item) => !item.hiddenInProd || !isProd),
    [isProd],
  );

  const itemHighlighted = useMemo(() => {
    const exact = visibleMenuItems.find((e) => e.link === fullPath);
    if (exact) return exact.label;
    const byPathname = visibleMenuItems.find(
      (e) => !e.link.includes('?') && normalizedPathname === e.link,
    );
    return byPathname?.label || '/';
  }, [fullPath, normalizedPathname, visibleMenuItems]);

  const handleToggleBurgerMenu = () => setIsBurgerOpen((prev) => !prev);

  useEffect(() => {
    if (isBurgerOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isBurgerOpen]);

  return (
    <>
      <Container>
        <MenuBlockWrapper>
          <Logo href="/" aria-label="Alleimmobilien — go to homepage">
            <LogoHeader aria-hidden="true" />
          </Logo>
          <MenuWrapper>
            <DesktopNav>
              {visibleMenuItems.map(({ label, link }) => (
                <LinkStyled key={label} href={link}>
                  <Typography
                    fontSize={16}
                    lineHeight={1}
                    fontWeight={itemHighlighted === label ? 800 : 600}
                  >
                    {t(label)}
                  </Typography>
                </LinkStyled>
              ))}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '24px',
                }}
              >
                <AuthButton />
                {!isProd && <LanguageSwitcher />}
              </Box>
            </DesktopNav>
            <MobileControls>
              <Button
                variant="transparent"
                onClick={handleToggleBurgerMenu}
                disableRipple
                aria-label={isBurgerOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isBurgerOpen}
              >
                <div
                  className={`menu burgerBtn ${isBurgerOpen ? 'open' : ''}`}
                  data-menu="1"
                  aria-hidden="true"
                >
                  <div className="icon-left"></div>
                  <div className="icon-right"></div>
                </div>
              </Button>
            </MobileControls>
          </MenuWrapper>
        </MenuBlockWrapper>
      </Container>

      <BurgerMenu
        isOpen={isBurgerOpen}
        setIsOpen={setIsBurgerOpen}
        itemHighlighted={itemHighlighted}
        menuItems={visibleMenuItems}
      />
    </>
  );
};
